/**
 * ResearchHubModal
 *
 * OPO Research Department hub modal.
 * Tabs: Active Tasks | Scheduled | Knowledge Base | Settings
 *
 * Mirrors the FundRaisingHubModal structural pattern.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';

const TABS = ['Active Tasks', 'Scheduled', 'Knowledge Base', 'Settings'];
const POLL_MS = 2000;

const STATUS_COLOR = {
  queued:    '#8b949e',
  running:   '#58a6ff',
  complete:  '#3fb950',
  failed:    '#f85149',
  cancelled: '#8b949e',
  abandoned: '#f85149',
};

function Badge({ status }) {
  const color = STATUS_COLOR[status] || '#8b949e';
  return (
    <span style={{
      fontSize: 11, padding: '2px 7px', borderRadius: 10,
      background: '#21262d', color, fontWeight: 500,
    }}>
      {status}
    </span>
  );
}

function ProgressBar({ pct, status }) {
  const color = STATUS_COLOR[status] || '#8b949e';
  return (
    <div style={{ background: '#21262d', borderRadius: 3, height: 4, marginTop: 4, overflow: 'hidden' }}>
      <div style={{ height: '100%', width: `${pct || 0}%`, background: color, borderRadius: 3, transition: 'width 0.4s' }} />
    </div>
  );
}

// ── Active Tasks tab ──────────────────────────────────────────────────────────

function ActiveTasksTab({ userId, token }) {
  const [tasks,   setTasks]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [newQuery, setNewQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const pollRef = useRef(null);

  const fetchTasks = useCallback(async () => {
    try {
      const res = await fetch(`/api/opo/research/tasks?user_id=${userId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const data = await res.json();
        setTasks(data.tasks || []);
      }
    } catch (_) {}
    setLoading(false);
  }, [userId, token]);

  useEffect(() => {
    fetchTasks();
    pollRef.current = setInterval(fetchTasks, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchTasks]);

  const handleCreate = async () => {
    if (!newQuery.trim()) return;
    setCreating(true);
    try {
      const res = await fetch('/api/opo/research/tasks', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ query: newQuery, user_id: userId }),
      });
      if (res.ok) {
        setNewQuery('');
        fetchTasks();
      }
    } catch (_) {}
    setCreating(false);
  };

  const handleCancel = async (taskId) => {
    await fetch(`/api/opo/research/tasks/${taskId}/cancel`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    fetchTasks();
  };

  return (
    <div style={{ padding: '16px 0' }}>
      {/* New task form */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <input
          value={newQuery}
          onChange={e => setNewQuery(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleCreate()}
          placeholder="Ask a research question…"
          style={{
            flex: 1, padding: '8px 12px', borderRadius: 6,
            border: '1px solid #30363d', background: '#0d1117',
            color: '#c9d1d9', fontSize: 13, outline: 'none',
          }}
        />
        <button
          onClick={handleCreate}
          disabled={creating || !newQuery.trim()}
          style={{
            padding: '8px 16px', borderRadius: 6, border: 'none',
            background: creating ? '#21262d' : '#238636', color: '#fff',
            fontSize: 13, cursor: creating ? 'not-allowed' : 'pointer',
          }}
        >
          {creating ? 'Starting…' : 'Start Research'}
        </button>
      </div>

      {loading && <div style={{ color: '#8b949e', fontSize: 13 }}>Loading tasks…</div>}

      {!loading && tasks.length === 0 && (
        <div style={{ color: '#8b949e', fontSize: 13 }}>
          No research tasks yet. Start a new one above.
        </div>
      )}

      {tasks.map(task => (
        <div key={task.task_id} style={{
          background: '#161b22', border: '1px solid #30363d', borderRadius: 8,
          padding: '12px 14px', marginBottom: 10,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Badge status={task.status} />
            <span style={{ fontSize: 13, color: '#e6edf3', flex: 1 }}>
              {(task.query || '').slice(0, 100)}
            </span>
            <span style={{ fontSize: 11, color: '#8b949e' }}>
              {task.created_at ? new Date(task.created_at).toLocaleString() : ''}
            </span>
          </div>
          <ProgressBar pct={task.progress_pct} status={task.status} />
          {task.current_subtask && task.status === 'running' && (
            <div style={{ fontSize: 12, color: '#58a6ff', marginTop: 4 }}>
              {task.current_subtask}
            </div>
          )}
          {task.error && (
            <div style={{ fontSize: 12, color: '#f85149', marginTop: 4 }}>{task.error}</div>
          )}
          {task.status === 'running' && (
            <button
              onClick={() => handleCancel(task.task_id)}
              style={{
                marginTop: 6, padding: '3px 10px', borderRadius: 4,
                border: '1px solid #f85149', background: 'transparent',
                color: '#f85149', fontSize: 11, cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Scheduled tab ─────────────────────────────────────────────────────────────

function ScheduledTab({ userId, token }) {
  const [scheduled, setScheduled]   = useState([]);
  const [loading,   setLoading]     = useState(true);
  const [form, setForm]             = useState({ query: '', cron_expr: '0 8 * * 1', email: '' });
  const [saving, setSaving]         = useState(false);

  const fetch_ = useCallback(async () => {
    try {
      const res = await fetch(`/api/opo/research/scheduled?user_id=${userId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const data = await res.json();
        setScheduled(data.tasks || []);
      }
    } catch (_) {}
    setLoading(false);
  }, [userId, token]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const handleCreate = async () => {
    if (!form.query.trim()) return;
    setSaving(true);
    try {
      const res = await fetch('/api/opo/research/scheduled', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          user_id:         userId,
          query:           form.query,
          cron_expr:       form.cron_expr,
          email_digest_to: form.email || undefined,
        }),
      });
      if (res.ok) { setForm({ query: '', cron_expr: '0 8 * * 1', email: '' }); fetch_(); }
    } catch (_) {}
    setSaving(false);
  };

  const handleDelete = async (id) => {
    await fetch(`/api/opo/research/scheduled/${id}`, {
      method: 'DELETE',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    fetch_();
  };

  const inputStyle = {
    width: '100%', padding: '7px 10px', borderRadius: 5,
    border: '1px solid #30363d', background: '#0d1117',
    color: '#c9d1d9', fontSize: 13, outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div style={{ padding: '16px 0' }}>
      {/* Create form */}
      <div style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: 8, padding: 14, marginBottom: 20 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: '#e6edf3', marginBottom: 10 }}>
          Schedule a recurring research task
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <input
            value={form.query} placeholder="Research question…"
            onChange={e => setForm(f => ({ ...f, query: e.target.value }))}
            style={inputStyle}
          />
          <input
            value={form.cron_expr} placeholder="Cron expression (e.g. 0 8 * * 1 = Mon 8am)"
            onChange={e => setForm(f => ({ ...f, cron_expr: e.target.value }))}
            style={inputStyle}
          />
          <input
            value={form.email} placeholder="Email digest to (optional)"
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            style={inputStyle}
          />
          <button
            onClick={handleCreate} disabled={saving || !form.query.trim()}
            style={{
              padding: '8px 14px', borderRadius: 6, border: 'none',
              background: saving ? '#21262d' : '#238636', color: '#fff',
              fontSize: 13, cursor: saving ? 'not-allowed' : 'pointer', alignSelf: 'flex-start',
            }}
          >
            {saving ? 'Saving…' : 'Add Schedule'}
          </button>
        </div>
      </div>

      {loading && <div style={{ color: '#8b949e', fontSize: 13 }}>Loading…</div>}
      {!loading && scheduled.length === 0 && (
        <div style={{ color: '#8b949e', fontSize: 13 }}>No scheduled tasks yet.</div>
      )}
      {scheduled.map(t => (
        <div key={t.id} style={{
          background: '#161b22', border: '1px solid #30363d', borderRadius: 8,
          padding: '10px 14px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, color: '#e6edf3' }}>{t.query?.slice(0, 80)}</div>
            <div style={{ fontSize: 11, color: '#8b949e', marginTop: 2 }}>
              {t.cron_expr} {t.email_digest_to ? `→ ${t.email_digest_to}` : ''}
            </div>
          </div>
          <span style={{
            fontSize: 11, padding: '2px 7px', borderRadius: 10,
            background: t.active ? '#1a3d24' : '#21262d',
            color: t.active ? '#3fb950' : '#8b949e',
          }}>
            {t.active ? 'Active' : 'Paused'}
          </span>
          <button
            onClick={() => handleDelete(t.id)}
            style={{
              padding: '3px 9px', borderRadius: 4, border: '1px solid #f85149',
              background: 'transparent', color: '#f85149', fontSize: 11, cursor: 'pointer',
            }}
          >
            Delete
          </button>
        </div>
      ))}
    </div>
  );
}

// ── Knowledge Base tab ────────────────────────────────────────────────────────

function KnowledgeBaseTab({ userId, token }) {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`/api/opo/research/kb?user_id=${userId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const data = await res.json();
          setItems(data.items || []);
        }
      } catch (_) {}
      setLoading(false);
    })();
  }, [userId, token]);

  const handleDelete = async (id) => {
    await fetch(`/api/opo/research/kb/${id}`, {
      method: 'DELETE',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    setItems(prev => prev.filter(i => i.id !== id));
  };

  if (loading) return <div style={{ color: '#8b949e', fontSize: 13, padding: '16px 0' }}>Loading…</div>;
  if (items.length === 0) return (
    <div style={{ color: '#8b949e', fontSize: 13, padding: '16px 0' }}>
      Completed research tasks that you save will appear here.
    </div>
  );

  return (
    <div style={{ padding: '16px 0' }}>
      {items.map(item => (
        <div key={item.id} style={{
          background: '#161b22', border: '1px solid #30363d', borderRadius: 8,
          padding: '12px 14px', marginBottom: 10,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#e6edf3' }}>{item.title}</div>
              {item.summary && (
                <div style={{ fontSize: 12, color: '#8b949e', marginTop: 4 }}>{item.summary?.slice(0, 200)}</div>
              )}
              <div style={{ fontSize: 11, color: '#58a6ff', marginTop: 4 }}>
                {new Date(item.created_at).toLocaleDateString()}
              </div>
            </div>
            <button
              onClick={() => handleDelete(item.id)}
              style={{
                padding: '2px 8px', borderRadius: 4, border: '1px solid #30363d',
                background: 'transparent', color: '#8b949e', fontSize: 11, cursor: 'pointer',
              }}
            >
              Remove
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Settings tab ──────────────────────────────────────────────────────────────

function SettingsTab() {
  return (
    <div style={{ padding: '20px 0', color: '#8b949e', fontSize: 13 }}>
      <div style={{ marginBottom: 12 }}>
        <strong style={{ color: '#e6edf3' }}>Research Mode</strong>
        <div style={{ marginTop: 4 }}>
          Research mode is enabled for your account. Complex strategic queries will automatically
          be routed to the async research pipeline instead of the synchronous chat loop.
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <strong style={{ color: '#e6edf3' }}>Models</strong>
        <div style={{ marginTop: 4 }}>
          Planner: Qwen 14B · Synthesizer: Qwen 32B · Classifier: Qwen 3B (tiebreaker)
        </div>
      </div>
      <div>
        <strong style={{ color: '#e6edf3' }}>Budget</strong>
        <div style={{ marginTop: 4 }}>Maximum 5 minutes per task · 7 subtasks · 3 web browse calls</div>
      </div>
    </div>
  );
}

// ── Main modal ────────────────────────────────────────────────────────────────

export default function ResearchHubModal({ isOpen, onClose, userId, token }) {
  const [activeTab, setActiveTab] = useState('Active Tasks');

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999, fontFamily: 'system-ui, sans-serif',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: '#0d1117', border: '1px solid #30363d', borderRadius: 12,
        width: '92%', maxWidth: 740, maxHeight: '88vh', display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          padding: '18px 20px 0', display: 'flex', alignItems: 'center',
          borderBottom: '1px solid #21262d',
        }}>
          <span style={{ fontSize: 20, marginRight: 10 }}>🔬</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#e6edf3' }}>Research Department</div>
            <div style={{ fontSize: 12, color: '#8b949e' }}>
              Async multi-step research powered by Cornelius
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent', border: 'none', color: '#8b949e',
              fontSize: 22, cursor: 'pointer', padding: '0 4px',
            }}
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, padding: '0 20px', borderBottom: '1px solid #21262d' }}>
          {TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '10px 14px', background: 'transparent', border: 'none',
                borderBottom: activeTab === tab ? '2px solid #58a6ff' : '2px solid transparent',
                color: activeTab === tab ? '#e6edf3' : '#8b949e',
                fontSize: 13, cursor: 'pointer', fontWeight: activeTab === tab ? 600 : 400,
                transition: 'color 0.15s',
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px 16px' }}>
          {activeTab === 'Active Tasks' && <ActiveTasksTab userId={userId} token={token} />}
          {activeTab === 'Scheduled'    && <ScheduledTab   userId={userId} token={token} />}
          {activeTab === 'Knowledge Base' && <KnowledgeBaseTab userId={userId} token={token} />}
          {activeTab === 'Settings'     && <SettingsTab />}
        </div>
      </div>
    </div>
  );
}
